import {
  CheckCircleFilled,
  CloseOutlined,
  ExclamationCircleFilled,
  LoadingOutlined
} from "@ant-design/icons"
import { Button, Card, Flex, Progress, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { type UploadQueueItem } from "../hooks/useUploadQueue.ts"

const { Text } = Typography

interface UploadQueuePanelProps {
  items: UploadQueueItem[]
  onRetry: (id: string) => void
  onCancelOne: (id: string) => void
  onCancelAll: () => void
  onClose: () => void
}

const statusIcon = (status: UploadQueueItem["status"]) => {
  switch (status) {
    case "completed":
      return <CheckCircleFilled className="text-success" />
    case "failed":
      return <ExclamationCircleFilled className="text-error" />
    case "cancelled":
      return <ExclamationCircleFilled className="text-secondary" />
    default:
      return <LoadingOutlined />
  }
}

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function UploadQueuePanel({
  items,
  onRetry,
  onCancelOne,
  onCancelAll,
  onClose
}: UploadQueuePanelProps) {
  const { t } = useTranslation()
  if (!items.length) return null

  const active = items.some((item) => item.status === "queued" || item.status === "uploading")

  return (
    <Card
      size="small"
      style={{
        position: "fixed",
        right: 24,
        bottom: 24,
        width: 340,
        zIndex: 1000,
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)"
      }}
      styles={{ body: { padding: 0 } }}
    >
      <Flex
        align="center"
        justify="space-between"
        className="border-b border-border"
        style={{ padding: "10px 12px" }}
      >
        <Text strong>{t("files.uploadQueue.title", { count: items.length })}</Text>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          aria-label={t("files.uploadQueue.close")}
          onClick={onClose}
        />
      </Flex>

      <Flex vertical style={{ maxHeight: 320, overflowY: "auto" }}>
        {items.map((item) => (
          <Flex
            key={item.id}
            vertical
            gap={2}
            className="border-b border-border"
            style={{ padding: "8px 12px" }}
          >
            <Flex align="center" gap={8}>
              {statusIcon(item.status)}
              <Text ellipsis={{ tooltip: item.file.name }} className="text-sm" style={{ flex: 1 }}>
                {item.file.name}
              </Text>
              {(item.status === "queued" || item.status === "uploading") && (
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  aria-label={t("files.uploadQueue.cancel", { name: item.file.name })}
                  onClick={() => onCancelOne(item.id)}
                />
              )}
            </Flex>
            {item.status === "uploading" && (
              <Progress percent={item.progress} size="small" showInfo={false} />
            )}
            <Text type="secondary" className="text-xs">
              {item.status === "completed" &&
                t("files.uploadQueue.completed", { size: formatSize(item.file.size) })}
              {item.status === "uploading" &&
                t("files.uploadQueue.uploading", { percent: item.progress })}
              {item.status === "queued" && t("files.uploadQueue.queued")}
              {item.status === "cancelled" && t("files.uploadQueue.cancelled")}
              {item.status === "failed" &&
                (item.error ?? t("files.uploadQueue.failed"))}
            </Text>
            {item.status === "failed" && (
              <Button size="small" className="self-start" onClick={() => onRetry(item.id)}>
                {t("files.uploadQueue.retry")}
              </Button>
            )}
          </Flex>
        ))}
      </Flex>

      {active && (
        <Flex justify="end" style={{ padding: "8px 12px" }}>
          <Button size="small" danger onClick={onCancelAll}>
            {t("files.uploadQueue.cancelAll")}
          </Button>
        </Flex>
      )}
    </Card>
  )
}
